import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Modal, 
  Button, 
  Group, 
  Text, 
  SimpleGrid, 
  Stack, 
  Tabs, 
  Badge,
  LoadingOverlay
} from '@mantine/core';
import { PersonBetter } from '../../types/Person';
import { Face } from '../../types/Face';
import { fetchFacesForPerson, untrackFace, labelFace, removeIsPersonSuggestionForFace } from '../../api/Faces';
import { IconCheck, IconX } from '@tabler/icons-react';
import { FaceCard } from './FaceCard';
import ImageWithSkeleton from '../ImageWithSkeleton';

interface FacesForPersonModalProps {
  person: PersonBetter;
  onClose: () => void;
  allPersons?: PersonBetter[];
}

type FaceAction = 'confirm' | 'deny' | 'untrack' | 'unconfirm';

export const FacesForPersonModal: React.FC<FacesForPersonModalProps> = ({ person, onClose, allPersons = [] }) => {
  const [faces, setFaces] = useState<Face[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>('confirmed');
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [hoveredFace, setHoveredFace] = useState<string | null>(null);
  const [editingFace, setEditingFace] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');

  const modalContentRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const loadMoreFaces = useCallback(async (currentPage: number) => {
    if (loadingRef.current || !hasMoreRef.current) return;

    loadingRef.current = true;
    setLoading(true);

    try {
      // Use the updated API to fetch all faces for the person
      const response = await fetchFacesForPerson(person, currentPage, 20);
      setFaces(prev => [...prev, ...response.faces]);
      setHasMore(response.hasMore);
      hasMoreRef.current = response.hasMore;
    } catch (error) {
      console.error('Failed to load faces:', error);
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [person]);

  useEffect(() => {
    // Reset state when modal opens
    setFaces([]);
    setPage(1);
    setHasMore(true);
    hasMoreRef.current = true;
    setActiveTab('confirmed');
    setHoveredFace(null);
    setEditingFace(null);
    setSearchValue('');
    
    // Load initial faces
    loadMoreFaces(1);
  }, [person, loadMoreFaces]);

  const handleHoverFace = (faceKey: string) => {
    setHoveredFace(faceKey);
  };

  const handleLeaveFace = () => {
    setHoveredFace(null);
  };

  const handleEditFace = (faceKey: string) => {
    setEditingFace(faceKey);
    // If the face has a person suggestion, pre-fill the search value
    const face = faces.find(f => f.face_key === faceKey);
    if (face?.is_person?.person) {
      setSearchValue(`${face.is_person.person.first} ${face.is_person.person.last}`);
    } else {
      setSearchValue('');
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleOptionSubmit = (value: string) => {
    console.log('Selected person name:', value);
    // In a real implementation, we would call the API to update the person name
    setEditingFace(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setEditingFace(null);
    } else if (e.key === 'Enter') {
      handleOptionSubmit(searchValue);
    }
  };

  const handleFaceAction = async (action: FaceAction, face: Face) => {
    try {
      switch (action) {
        case 'confirm':
          console.log('Confirming face:', face.face_key);
          if (face.is_person?.person) {
            // In a real implementation, we would call the API to confirm the face
            // await labelFace(face, face.is_person.person);
            console.log('Would call labelFace API with:', face, face.is_person.person);
            
            // Update the face in the UI
            setFaces(prev => prev.map(f => 
              f.face_key === face.face_key 
                ? { ...f, is_person: { ...f.is_person!, confirmed: true } }
                : f
            ));
          }
          break;
        case 'unconfirm':
          console.log('Unconfirming face:', face.face_key);
          if (face.is_person?.person) {
            // In a real implementation, we would call the API to unconfirm the face
            // This would likely be a similar API call to labelFace but with confirmed set to false
            console.log('Would call API to unconfirm face with:', face);
            
            // Update the face in the UI
            setFaces(prev => prev.map(f => 
              f.face_key === face.face_key 
                ? { ...f, is_person: { ...f.is_person!, confirmed: false } }
                : f
            ));
          }
          break;
        case 'deny':
          console.log('Denying face:', face.face_key);
          // In a real implementation, we would call the API to deny the face
          // await removeIsPersonSuggestionForFace(face);
          console.log('Would call removeIsPersonSuggestionForFace API with:', face);
          
          // Update the face in the UI
          setFaces(prev => prev.map(f => 
            f.face_key === face.face_key 
              ? { ...f, is_person: { ...f.is_person!, confirmed: false } }
              : f
          ));
          break;
        case 'untrack':
          console.log('Untracking face:', face.face_key);
          // In a real implementation, we would call the API to untrack the face
          // await untrackFace(face);
          console.log('Would call untrackFace API with:', face);
          
          // Remove the face from the UI
          setFaces(prev => prev.filter(f => f.face_key !== face.face_key));
          break;
      }
    } catch (error) {
      console.error(`Error handling face action ${action}:`, error);
    }
  };

  const handleScroll = useCallback(() => {
    if (!modalContentRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = modalContentRef.current;

    if (scrollHeight - scrollTop <= clientHeight + 100) {
      if (!loadingRef.current && hasMoreRef.current) {
        setPage(prevPage => {
          loadMoreFaces(prevPage + 1);
          return prevPage + 1;
        });
      }
    }
  }, [loadMoreFaces]);

  // No mock data - we'll use the real data fetched by loadMoreFaces

  // Get counts for the tab badges
  const confirmedCount = faces.filter(face => face.is_person?.confirmed).length;
  const unconfirmedCount = faces.filter(face => face.is_person && !face.is_person.confirmed).length;

  return (
    <Modal
      opened={!!person}
      onClose={onClose}
      size="xl"
      title={`Faces for ${person ? `${person.first} ${person.last}` : 'Unknown'}`}
      styles={{
        content: {
          height: '85vh',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '85vh',
        },
        body: {
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          maxHeight: 'calc(85vh - 80px)', // Account for header and footer
        },
      }}
    >
      <Stack style={{ height: '100%', overflow: 'hidden', maxHeight: '100%' }}>
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab 
              value="confirmed" 
              leftSection={<IconCheck size={14} />}
              rightSection={
                <Badge size="sm" variant="filled" color="blue">
                  {confirmedCount}
                </Badge>
              }
            >
              Confirmed Faces
            </Tabs.Tab>
            <Tabs.Tab 
              value="unconfirmed" 
              leftSection={<IconX size={14} />}
              rightSection={
                <Badge size="sm" variant="filled" color="gray">
                  {unconfirmedCount}
                </Badge>
              }
            >
              Unconfirmed Faces
            </Tabs.Tab>
          </Tabs.List>
          
          <Tabs.Panel value="confirmed" pt="xs" style={{ height: '100%', overflow: 'hidden' }}>
            <div 
              ref={modalContentRef} 
              style={{ 
                position: 'relative', 
                height: '100%', 
                maxHeight: 'calc(85vh - 180px)', /* Account for header, tabs, and footer */
                overflow: 'auto',
                paddingBottom: '20px'
              }}
              onScroll={handleScroll}
            >
              <LoadingOverlay visible={loading && faces.length === 0} />
              <SimpleGrid cols={4} spacing="xs" style={{ width: '100%' }}>
                {faces
                  .filter(face => face.is_person?.confirmed)
                  .map((face) => (
                    <div 
                      key={face.face_key} 
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        minHeight: '200px'
                      }}
                    >
                      <FaceCard
                        face={face}
                        isHovered={hoveredFace === face.face_key}
                        isEditing={editingFace === face.face_key}
                        onHover={handleHoverFace}
                        onLeave={handleLeaveFace}
                        onAction={(action) => handleFaceAction(action, face)}
                        onEdit={() => handleEditFace(face.face_key)}
                        autocompleteProps={{
                          searchValue,
                          suggestions: allPersons.map(p => `${p.first} ${p.last}`),
                          onSearchChange: handleSearchChange,
                          onOptionSubmit: handleOptionSubmit,
                          onKeyDown: handleKeyDown
                        }}
                      />
                    </div>
                  ))}
                {loading &&
                  Array.from({ length: 4 }).map((_, index) => (
                    <div 
                      key={`loading-${index}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        minHeight: '200px',
                        aspectRatio: '4 / 5'
                      }}
                    >
                      <ImageWithSkeleton
                        src={undefined}
                        alt="Loading..."
                        onClick={() => {}}
                      />
                    </div>
                  ))}
              </SimpleGrid>
              {!loading && faces.filter(face => face.is_person?.confirmed).length === 0 && (
                <Text ta="center" c="dimmed" py="xl">
                  No confirmed faces found for this person.
                </Text>
              )}
            </div>
          </Tabs.Panel>
          
          <Tabs.Panel value="unconfirmed" pt="xs" style={{ height: '100%', overflow: 'hidden' }}>
            <div
              style={{ 
                position: 'relative', 
                height: '100%', 
                maxHeight: 'calc(85vh - 180px)', /* Account for header, tabs, and footer */
                overflow: 'auto',
                paddingBottom: '20px'
              }}
            >
              <SimpleGrid cols={4} spacing="xs" style={{ width: '100%' }}>
                {faces
                  .filter(face => face.is_person && !face.is_person.confirmed)
                  .map((face) => (
                    <div 
                      key={face.face_key} 
                      style={{ 
                        position: 'relative', 
                        width: '100%', 
                        height: '100%',
                        minHeight: '200px'
                      }}
                    >
                      <FaceCard
                        face={face}
                        isHovered={hoveredFace === face.face_key}
                        isEditing={editingFace === face.face_key}
                        onHover={handleHoverFace}
                        onLeave={handleLeaveFace}
                        onAction={(action) => handleFaceAction(action, face)}
                        onEdit={() => handleEditFace(face.face_key)}
                        autocompleteProps={{
                          searchValue,
                          suggestions: allPersons.map(p => `${p.first} ${p.last}`),
                          onSearchChange: handleSearchChange,
                          onOptionSubmit: handleOptionSubmit,
                          onKeyDown: handleKeyDown
                        }}
                      />
                    </div>
                  ))}
              </SimpleGrid>
              
              {!loading && faces.filter(face => face.is_person && !face.is_person.confirmed).length === 0 && (
                <Text ta="center" c="dimmed" py="xl">
                  No unconfirmed faces available for this person.
                </Text>
              )}
            </div>
          </Tabs.Panel>
        </Tabs>
        
        <Group style={{ justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
};
